/**
 * Zustand-based Auth Store (Web implementation)
 * Matches the mobile app's authentication state management pattern
 * Adapted from fmc-digital-mobile-app/api/auth/src/store/useUserStore.ts
 */
import { create } from "zustand";
import { tokenStorage } from "../storage";
const initialUser = null;
export const initialAuthState = {
    authenticated: false,
    sessionLogout: false,
    stateToken: "",
    sessionToken: "",
    mfaFactors: [],
    user: initialUser,
    selectedFactor: null,
    factorsByGroup: {},
    isLoading: false,
    error: null,
    tokens: null,
};
/**
 * Group MFA factors by type for easy lookup
 */
export const groupFactorsByType = (factors) => {
    return factors.reduce((acc, curr) => {
        return Object.assign(acc, { [curr.factorType]: curr });
    }, {});
};
/**
 * Get the default MFA factor (selected one or first available)
 */
export const getDefaultFactor = (mfaFactors) => {
    if (mfaFactors.length === 0)
        return null;
    // Find a factor marked as selected, or default to first
    const selectedFactor = mfaFactors.find((factor) => factor.selected === true);
    return selectedFactor ?? mfaFactors[0];
};
/**
 * Main auth store using Zustand
 */
export const useAuthStore = create((set, get) => ({
    ...initialAuthState,
    setAuthenticated: (authenticated) => set({ authenticated }),
    setLoggedIn: (payload) => set({
        authenticated: true,
        user: payload.user,
        sessionToken: payload.sessionToken ?? "",
        tokens: payload.tokens ?? null,
        stateToken: "",
        mfaFactors: [],
        selectedFactor: null,
        factorsByGroup: {},
        isLoading: false,
        error: null,
    }),
    setMfaFactors: (payload) => {
        const factors = payload.factors ?? [];
        set({
            mfaFactors: factors,
            stateToken: payload.stateToken ?? "",
            sessionToken: "",
            selectedFactor: getDefaultFactor(factors),
            factorsByGroup: groupFactorsByType(factors),
            isLoading: false,
        });
    },
    setNewSelectedFactor: (factorId) => {
        const { mfaFactors } = get();
        const factor = mfaFactors.find((f) => f.id === factorId);
        if (!factor) {
            const availableIds = mfaFactors.map(f => f.id).join(', ');
            const errorMsg = `MFA factor with ID "${factorId}" not found. Available factors: [${availableIds ?? 'none'}]`;
            console.error(errorMsg);
            throw new Error(errorMsg);
        }
        set({ selectedFactor: factor });
    },
    clearMfaState: () => set({
        stateToken: "",
        mfaFactors: [],
        selectedFactor: null,
        factorsByGroup: {},
    }),
    logout: async () => {
        try {
            // Clear tokens from storage
            await tokenStorage.clearTokens();
        }
        catch (error) {
            console.error("Error during logout:", error);
        }
        set({
            ...initialAuthState,
            sessionLogout: true,
        });
    },
    resetSessionLogout: () => set({ sessionLogout: false }),
    resetUser: () => set(initialAuthState),
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    setTokens: (tokens) => set({ tokens }),
}));
// Convenience selectors (matching mobile app pattern)
/** Hook to get authenticated state */
export const useAuthenticated = () => useAuthStore((state) => state.authenticated);
/** Hook to get current user */
export const useUser = () => useAuthStore((state) => state.user);
/** Hook to get session logout flag */
export const useSessionLogout = () => useAuthStore((state) => state.sessionLogout);
/** Hook to get MFA factors */
export const useMfaFactors = () => useAuthStore((state) => state.mfaFactors);
/** Hook to get selected MFA factor */
export const useSelectedFactor = () => useAuthStore((state) => state.selectedFactor);
/** Hook to get loading state */
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
/** Hook to get error state */
export const useAuthError = () => useAuthStore((state) => state.error);
/** Hook to get auth tokens */
export const useAuthTokens = () => useAuthStore((state) => state.tokens);
/** Hook to get full auth state */
export const useAuthState = () => useAuthStore((state) => ({
    authenticated: state.authenticated,
    user: state.user,
    isLoading: state.isLoading,
    error: state.error,
    tokens: state.tokens,
    mfaFactors: state.mfaFactors,
    selectedFactor: state.selectedFactor,
    stateToken: state.stateToken,
}));
//# sourceMappingURL=useAuthStore.js.map